import { useField, useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import useOutsideClick from '../../../hooks/ui-control/useOutsideClick';
import CloseInCircleIcon from '../../Icons/CloseInCircleIcon';
import { FieldErrorInfo, Input, InputLabel, InputWrapper } from '../FormField';
import EmptyStateContainer from './EmptyStateContainer';

interface CompoundSearchSelectProps {
  label?: any;
  id: string;
  name: string;
  placeholder?: string;
  className?: string;
  readonly?: boolean;
  onChange?: (values: string[]) => void;
  onSelect?: (data: Record<string, any>[]) => void;
  renderComponent?: (...args: any) => React.ReactNode;
  fetchDataFn?: (...args: any) => void;
  fetchDataLoading?: boolean;
  fetchedData?: any[];
  requiredDataKey?: string;
  presentationDataKey?: string;
  showErrorLabel?: boolean;
  allowValidEmail?: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CompoundSearchSelect: React.FC<CompoundSearchSelectProps> = ({
  label,
  className,
  onChange,
  onSelect,
  renderComponent,
  fetchDataFn,
  fetchDataLoading,
  fetchedData,
  requiredDataKey,
  presentationDataKey,
  showErrorLabel = true,
  allowValidEmail,
  ...props
}) => {
  const selectFieldRef = useRef<HTMLDivElement>(null);
  const [isAddContactModalOpen, setIsAddContactModalOpen] =
    useState<boolean>(false);

  useOutsideClick(
    selectFieldRef,
    () => setDidTouchSelectField(false),
    isAddContactModalOpen,
  );

  const { handleBlur } = useFormikContext<any>();
  const [field, meta, helper] = useField(props);
  const [selectFieldValue, setSelectFieldValue] = useState<string>('');
  const [didTouchSelectField, setDidTouchSelectField] =
    useState<boolean>(false);
  const [optionData, setOptionData] = useState<any[]>(fetchedData ?? []);
  // selectedItemsMap: requiredValue → full data object
  const [selectedItemsMap, setSelectedItemsMap] = useState<
    Record<string, Record<string, any>>
  >({});
  const [hasInvalidInput, setHasInvalidInput] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasInitiallyFetchedRef = useRef<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const chipsScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOptionData(fetchedData ?? []);
  }, [fetchedData]);

  useEffect(() => {
    const el = chipsScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [selectedItemsMap]);

  // Sync selectedItemsMap with field.value (handles external clear and remount after collapse)
  useEffect(() => {
    const fieldValues: string[] = Array.isArray(field.value) ? field.value : [];
    if (fieldValues.length === 0 && Object.keys(selectedItemsMap).length > 0) {
      setSelectedItemsMap({});
      return;
    }
    const missingValues = fieldValues.filter((val) => !selectedItemsMap[val]);
    if (missingValues.length > 0) {
      setSelectedItemsMap((prev) => {
        const newMap = { ...prev };
        missingValues.forEach((val) => {
          const match = requiredDataKey
            ? optionData.find((item) => item[requiredDataKey] === val)
            : null;
          const syntheticData: Record<string, any> = {};
          if (requiredDataKey) syntheticData[requiredDataKey] = val;
          if (presentationDataKey) syntheticData[presentationDataKey] = val;
          newMap[val] = match ?? syntheticData;
        });
        return newMap;
      });
    }
    // eslint-disable-next-line
  }, [field.value]);

  // Pre-populate chips when optionData arrives and field already has values (edit mode)
  useEffect(() => {
    if (!requiredDataKey || optionData.length === 0) return;
    const fieldValues: string[] = Array.isArray(field.value) ? field.value : [];
    if (fieldValues.length === 0) return;

    setSelectedItemsMap((prev) => {
      const newMap = { ...prev };
      let changed = false;
      fieldValues.forEach((val: string) => {
        if (!newMap[val]) {
          const match = optionData.find(
            (item) => item[requiredDataKey] === val,
          );
          if (match) {
            newMap[val] = match;
            changed = true;
          }
        }
      });
      return changed ? newMap : prev;
    });
    // eslint-disable-next-line
  }, [optionData]);

  const handleSelectFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSelectFieldValue(value);
    setHasInvalidInput(false);
    setDidTouchSelectField(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      if (fetchDataFn) fetchDataFn(value);
    }, 600);
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
    setDidTouchSelectField(true);

    if (!hasInitiallyFetchedRef.current && fetchDataFn) {
      fetchDataFn(selectFieldValue || '');
      hasInitiallyFetchedRef.current = true;
    }
  };

  const handleOptionClick = (data: Record<string, any>) => {
    const requiredValue = requiredDataKey
      ? data[requiredDataKey]
      : JSON.stringify(data);

    const newMap = { ...selectedItemsMap };
    if (newMap[requiredValue]) {
      delete newMap[requiredValue];
    } else {
      newMap[requiredValue] = data;
    }

    const values = Object.keys(newMap);
    setSelectedItemsMap(newMap);
    helper.setValue(values);
    setSelectFieldValue('');
    if (onChange) onChange(values);
    if (onSelect) onSelect(Object.values(newMap));
  };

  const handleRemoveSelected = (e: React.MouseEvent, requiredValue: string) => {
    e.stopPropagation();
    const newMap = { ...selectedItemsMap };
    delete newMap[requiredValue];
    const values = Object.keys(newMap);
    setSelectedItemsMap(newMap);
    helper.setValue(values);
    if (onChange) onChange(values);
    if (onSelect) onSelect(Object.values(newMap));
  };

  const addManualValue = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (allowValidEmail && !EMAIL_REGEX.test(trimmed)) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const syntheticData: Record<string, any> = {};
    if (requiredDataKey) syntheticData[requiredDataKey] = trimmed;
    if (presentationDataKey) syntheticData[presentationDataKey] = trimmed;
    const newMap = { ...selectedItemsMap, [trimmed]: syntheticData };
    const values = Object.keys(newMap);
    setSelectedItemsMap(newMap);
    helper.setValue(values);
    setSelectFieldValue('');
    setDidTouchSelectField(false);
    if (onChange) onChange(values);
    if (onSelect) onSelect(Object.values(newMap));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addManualValue(selectFieldValue);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text');
    const tokens = pasted.split(/[\s,;]+/).filter(Boolean);
    if (tokens.length === 0) return;
    e.preventDefault();
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const newMap = { ...selectedItemsMap };
    tokens.forEach((token) => {
      const trimmed = token.trim();
      if (!trimmed) return;
      if (allowValidEmail && !EMAIL_REGEX.test(trimmed)) return;
      const syntheticData: Record<string, any> = {};
      if (requiredDataKey) syntheticData[requiredDataKey] = trimmed;
      if (presentationDataKey) syntheticData[presentationDataKey] = trimmed;
      newMap[trimmed] = syntheticData;
    });
    const values = Object.keys(newMap);
    setSelectedItemsMap(newMap);
    helper.setValue(values);
    setSelectFieldValue('');
    setDidTouchSelectField(false);
    if (onChange) onChange(values);
    if (onSelect) onSelect(Object.values(newMap));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <InputWrapper className={className}>
      {label && (
        <InputLabel as='div' htmlFor={props.name || props.id}>
          {label}
        </InputLabel>
      )}
      <InputContainer
        ref={selectFieldRef}
        className={didTouchSelectField ? 'active' : ''}
        onClick={handleContainerClick}
      >
        <ChipsScrollArea ref={chipsScrollRef}>
          {Object.entries(selectedItemsMap).map(([requiredValue, data]) => {
            const chipLabel = presentationDataKey
              ? data[presentationDataKey]
              : requiredValue;
            return (
              <SelectedOptionItem key={requiredValue}>
                {chipLabel}
                <IconWrapper
                  onClick={(e) => handleRemoveSelected(e, requiredValue)}
                >
                  <CloseInCircleIcon />
                </IconWrapper>
              </SelectedOptionItem>
            );
          })}
          <SelectFieldBox
            placeholder={
              Object.keys(selectedItemsMap).length === 0
                ? (props.placeholder ?? '')
                : ''
            }
            type='text'
            onChange={handleSelectFieldChange}
            value={selectFieldValue}
            name={props.name}
            id={props.name}
            ref={inputRef}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            $invalid={hasInvalidInput}
          />
        </ChipsScrollArea>

        {didTouchSelectField && (
          <SelectOptionBox>
            {optionData.length !== 0 ? (
              optionData.map((data: Record<string, any>, index: number) => {
                const requiredValue = requiredDataKey
                  ? data[requiredDataKey]
                  : JSON.stringify(data);
                const isSelected = !!selectedItemsMap[requiredValue];
                return (
                  <SelectOption
                    key={index}
                    handleOptionClick={() => handleOptionClick(data)}
                    isSelected={isSelected}
                  >
                    {renderComponent ? renderComponent(data) : <></>}
                  </SelectOption>
                );
              })
            ) : (
              <EmptyState>
                {fetchDataLoading ? (
                  <Em>Loading...</Em>
                ) : (
                  <EmptyStateContainer
                    onModalOpenChange={setIsAddContactModalOpen}
                    searchValue={selectFieldValue}
                  />
                )}
              </EmptyState>
            )}
          </SelectOptionBox>
        )}
      </InputContainer>
      {showErrorLabel && meta.touched && meta.error ? (
        <FieldErrorInfo>{meta.error.capitalize()}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export default CompoundSearchSelect;

interface SelectOptionProps {
  handleOptionClick: () => void;
  isSelected?: boolean;
  children: any;
}

export const SelectOption: React.FC<SelectOptionProps> = ({
  handleOptionClick,
  isSelected,
  children,
}) => {
  return (
    <Option
      onClick={handleOptionClick}
      className={isSelected ? 'isSelected' : ''}
    >
      {children}
    </Option>
  );
};

const SelectFieldBox = styled(Input)<{ $invalid?: boolean }>`
  border: none;
  border-radius: 0;
  padding: 0;
  height: auto;
  flex: 1;
  min-width: 120px;
  background: transparent;
  color: ${({ $invalid }) => ($invalid ? '#ff0500' : 'inherit')};

  &:focus {
    outline: none;
    border: none;
  }
`;

export const InputContainer = styled.div`
  position: relative;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 8px;
  padding: 6px 12px;
  background: white;
  cursor: text;

  &.active,
  &:focus-within {
    border-color: ${({ theme }) => theme.palette.logiratePrimary};
  }
`;

const ChipsScrollArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  max-height: 200px;
  overflow-y: auto;
`;

export const SelectOptionBox = styled.div`
  position: absolute;
  top: 100%;
  left: -1px;
  right: -1px;
  width: auto;
  max-height: 180px;
  display: flex;
  overflow: auto;
  flex-direction: column;
  gap: 4px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  box-shadow: 1px 4px 12px -1px rgba(44, 78, 39, 0.15);
  border-radius: 4px;
  background-color: white;
  margin-top: 4px;
  z-index: 10;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

export const Option = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  cursor: pointer;
  padding: 16px;
  font-size: 14px;

  &:hover {
    background-color: #f9fafb;
  }

  &:last-of-type {
    border: none;
  }

  &.isSelected {
    /* background-color: #fee2e2; */
    opacity: 0.4;
  }
`;

const EmptyState = styled.div`
  color: gray;
  width: 100%;
`;

const Em = styled.em`
  padding: 16px 10px;
  font-weight: 500;
  font-size: 14px;
  line-height: 30px;
`;

const SelectedOptionItem = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  font-weight: 400;
  color: black;
  font-size: 14px;
  padding: 6px 8px;
  gap: 6px;
  height: fit-content;
  align-self: center;
  background-color: ${({ theme }) => theme.palette.lightNeutralGray};
`;

const IconWrapper = styled.div`
  display: grid;
  place-content: center;
  color: ${({ theme }) => theme.palette.darkTextColor};
  cursor: pointer;
`;
